import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Lightbulb, Calculator } from 'lucide-react';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import Breadcrumbs from '../../../components/ui/Breadcrumbs';
import ToolSidebar from '../../../components/layout/ToolSidebar';
import { useTheme } from '../../../contexts/ThemeContext';
import { getToolById } from '../../../data/tools';

// Импорт компонентов калькулятора
import CalculationForm from './LightingCalculatorPage/components/CalculationForm';
import ResultsOverview from './LightingCalculatorPage/components/ResultsOverview';
import VisualizationSection from './LightingCalculatorPage/components/VisualizationSection';
import QuickAdjustmentSidebar from './LightingCalculatorPage/components/QuickAdjustmentSidebar';
import LampComparisonSidebar from './LightingCalculatorPage/components/LampComparisonSidebar';
import TipsSidebar from './LightingCalculatorPage/components/TipsSidebar';
import InfoContent from './LightingCalculatorPage/components/InfoContent';

// Определение начального состояния формы
export const initialFormData = {
  roomType: 'livingRoom',
  roomLength: '5',
  roomWidth: '4',
  roomHeight: '2.7',
  targetIlluminance: '300',
  reflectionCeiling: '0.7',
  reflectionWalls: '0.5',
  reflectionFloor: '0.3',
  lampType: 'led',
  lampEfficiency: '100',
  lampPower: '10',
  lampFlux: '1000',
  lampCount: '1',
  maintenanceFactor: '0.8',
  utilizationFactor: '0.6',
  ceilingMountType: 'standard',
  customLampEnabled: false,
  colorTemperature: '4000',
  colorRendering: '80',
  energyEfficiencyClass: 'A',
  roomActivity: 'residential',
  calculationMethod: 'lumen'
};

// Определение типов помещений
export const roomTypes = [
  { id: 'livingRoom', name: 'Жилая комната', defaultIlluminance: 300 },
  { id: 'kitchen', name: 'Кухня', defaultIlluminance: 400 },
  { id: 'bedroom', name: 'Спальня', defaultIlluminance: 200 },
  { id: 'bathroom', name: 'Ванная комната', defaultIlluminance: 200 },
  { id: 'office', name: 'Офис', defaultIlluminance: 500 },
  { id: 'classroom', name: 'Учебный класс', defaultIlluminance: 500 },
  { id: 'industrial', name: 'Производственное помещение', defaultIlluminance: 500 },
  { id: 'corridor', name: 'Коридор', defaultIlluminance: 150 },
  { id: 'storage', name: 'Склад', defaultIlluminance: 200 },
  { id: 'custom', name: 'Другое (своё значение)', defaultIlluminance: 300 }
];

// Определение типов ламп
export const lampTypes = [
  { id: 'led', name: 'Светодиодная (LED)', defaultEfficiency: 100, defaultLampPower: 10, defaultFlux: 1000 },
  { id: 'fluorescent', name: 'Люминесцентная', defaultEfficiency: 70, defaultLampPower: 18, defaultFlux: 1260 },
  { id: 'halogen', name: 'Галогенная', defaultEfficiency: 20, defaultLampPower: 50, defaultFlux: 1000 },
  { id: 'incandescent', name: 'Лампа накаливания', defaultEfficiency: 15, defaultLampPower: 75, defaultFlux: 1125 },
  { id: 'metalHalide', name: 'Металлогалогенная', defaultEfficiency: 90, defaultLampPower: 70, defaultFlux: 6300 },
  { id: 'custom', name: 'Пользовательская', defaultEfficiency: 80, defaultLampPower: 20, defaultFlux: 1600 }
];

// Определение типов деятельности
export const activityTypes = [
  { id: 'residential', name: 'Жилое помещение' },
  { id: 'office', name: 'Офисное помещение' },
  { id: 'educational', name: 'Образовательное учреждение' },
  { id: 'industrial', name: 'Производственное помещение' },
  { id: 'commercial', name: 'Торговое помещение' },
  { id: 'healthcare', name: 'Медицинское учреждение' },
  { id: 'custom', name: 'Другое' }
];

// Определение российских норм освещённости согласно СП 52.13330.2016
export const russianLightingStandards = {
  residential: {
    livingRoom: { min: 150, recommended: 300 },
    kitchen: { min: 200, recommended: 400 },
    bedroom: { min: 150, recommended: 200 },
    bathroom: { min: 150, recommended: 200 },
    corridor: { min: 100, recommended: 150 }
  },
  office: {
    workspace: { min: 300, recommended: 500 },
    meetingRoom: { min: 300, recommended: 500 },
    reception: { min: 300, recommended: 400 },
    computerWork: { min: 400, recommended: 500 }
  },
  educational: {
    classroom: { min: 400, recommended: 500 },
    readingRoom: { min: 400, recommended: 500 },
    laboratory: { min: 500, recommended: 600 },
    workshop: { min: 400, recommended: 500 }
  },
  industrial: {
    rough: { min: 200, recommended: 300 },
    medium: { min: 300, recommended: 500 },
    fine: { min: 500, recommended: 750 },
    precise: { min: 750, recommended: 1000 }
  },
  commercial: {
    salesArea: { min: 300, recommended: 500 },
    checkout: { min: 500, recommended: 750 },
    display: { min: 500, recommended: 750 }
  },
  healthcare: {
    waitingRoom: { min: 200, recommended: 300 },
    examination: { min: 500, recommended: 750 },
    surgery: { min: 750, recommended: 1000 }
  }
};

/**
 * LightingCalculatorPage Component - Полный калькулятор освещённости для расчета параметров освещения
 * в различных типах помещений в соответствии с российскими стандартами.
 */
const LightingCalculatorPage = () => {
  const { subToolId } = useParams();
  const { darkMode, toggleDarkMode } = useTheme();

  // --- Данные инструмента ---
  const tool = useMemo(() => getToolById('lighting-calculator'), []);
  const pageTitle = tool?.metaTitle || 'Калькулятор освещённости | Gensio';
  const metaDescription = tool?.metaDescription || 'Расчет необходимого количества светильников и их мощности для оптимального освещения помещений в соответствии с российскими нормами СП 52.13330.2016 и СанПиН.';

  // --- Управление состоянием ---
  const [formData, setFormData] = useState(initialFormData);
  const [results, setResults] = useState(null);
  const [errors, setErrors] = useState({});
  const [isCalculating, setIsCalculating] = useState(false);
  const [savedLighting, setSavedLighting] = useState([]);
  const [customIlluminance, setCustomIlluminance] = useState(300);

  // --- Обновление заголовка и мета-описания ---
  useEffect(() => {
    document.title = pageTitle;
    let metaTag = document.querySelector('meta[name="description"]');
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.name = 'description';
      document.head.appendChild(metaTag);
    }
    metaTag.setAttribute('content', metaDescription);
  }, [pageTitle, metaDescription]);

  // --- Загрузка сохраненных расчетов из localStorage ---
  useEffect(() => {
    const savedLightingData = localStorage.getItem('savedLighting');
    if (savedLightingData) {
      try {
        setSavedLighting(JSON.parse(savedLightingData));
      } catch (e) {
        console.error("Error loading saved lighting calculations:", e);
      }
    }
  }, []);

  // --- Обработка subToolId для предварительной настройки формы ---
  useEffect(() => {
    if (!subToolId) return;

    setFormData(prev => {
      let newState = { ...initialFormData };

      switch (subToolId) {
        case 'residential':
          newState.roomActivity = 'residential';
          newState.roomType = 'livingRoom';
          newState.targetIlluminance = '300';
          break;
        case 'office':
          newState.roomActivity = 'office';
          newState.roomType = 'office';
          newState.targetIlluminance = '500';
          break;
        case 'industrial':
          newState.roomActivity = 'industrial';
          newState.roomType = 'industrial';
          newState.targetIlluminance = '500';
          break;
        case 'led':
          newState.lampType = 'led';
          break;
        case 'energy-saving':
          newState.lampType = 'led';
          newState.energyEfficiencyClass = 'A';
          break;
        case 'point-method':
          newState.calculationMethod = 'point';
          break;
        default:
          newState = { ...prev };
      }
      return newState;
    });

    setResults(null);
    setErrors({});

  }, [subToolId]);

  // Сохранение расчета в localStorage
  const saveLighting = (name) => {
    if (!results) return;

    const newSavedLighting = {
      id: Date.now().toString(),
      name: name || `Освещение ${formData.roomType} (${formData.roomLength}×${formData.roomWidth} м)`,
      date: new Date().toISOString(),
      formData: { ...formData },
      results: { ...results }
    };

    const updatedSavedLighting = [...savedLighting, newSavedLighting];
    setSavedLighting(updatedSavedLighting);
    localStorage.setItem('savedLighting', JSON.stringify(updatedSavedLighting));

    return newSavedLighting;
  };

  // Удаление сохраненного расчета
  const deleteSavedLighting = (id) => {
    const updatedSavedLighting = savedLighting.filter(lighting => lighting.id !== id);
    setSavedLighting(updatedSavedLighting);
    localStorage.setItem('savedLighting', JSON.stringify(updatedSavedLighting));
  };

  // Загрузка сохраненного расчета
  const loadSavedLighting = (id) => {
    const lightingToLoad = savedLighting.find(lighting => lighting.id === id);
    if (lightingToLoad) {
      setFormData(lightingToLoad.formData);
      setResults(lightingToLoad.results);
    }
  };

  // --- RENDER ---
  return (
    <div className={`min-h-screen ${darkMode ? 'dark' : ''} bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200 font-sans`}>
      {/* Background Pattern */}
      <div aria-hidden="true" className="fixed inset-0 bg-[radial-gradient(theme(colors.gray.200)_1px,transparent_1px)] dark:bg-[radial-gradient(theme(colors.gray.800)_1px,transparent_1px)] [background-size:24px_24px] opacity-20 dark:opacity-10 pointer-events-none z-[-1]"></div>

      <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumbs />

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* --- Main Content Area --- */}
          <div className="lg:w-2/3 flex-shrink-0">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-3">
                <Lightbulb className="w-8 h-8 text-amber-500 dark:text-amber-400 flex-shrink-0" />
                {tool?.name || 'Калькулятор освещённости'}
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                {metaDescription}
              </p>
            </div>

            {/* --- Форма калькулятора --- */}
            <CalculationForm 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              setErrors={setErrors}
              isCalculating={isCalculating}
              setIsCalculating={setIsCalculating}
              results={results}
              setResults={setResults}
              roomTypes={roomTypes}
              lampTypes={lampTypes}
              activityTypes={activityTypes}
              saveLighting={saveLighting}
              russianLightingStandards={russianLightingStandards}
              customIlluminance={customIlluminance}
              setCustomIlluminance={setCustomIlluminance}
            />

            {/* --- Результаты расчета --- */}
            {results && !isCalculating && (
              <>
                <ResultsOverview results={results} formData={formData} />
                <VisualizationSection results={results} formData={formData} />
              </>
            )}

            {/* --- Информационный контент об освещении --- */}
            <InfoContent />
          </div>

          {/* --- Sidebar Area --- */}
          <aside className="lg:w-1/3 self-start space-y-8">
            {/* --- Панель быстрой настройки --- */}
            <QuickAdjustmentSidebar 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              isCalculating={isCalculating}
              roomTypes={roomTypes}
              lampTypes={lampTypes}
            />

            {/* --- Панель сравнения ламп --- */}
            <LampComparisonSidebar 
              savedLighting={savedLighting}
              deleteSavedLighting={deleteSavedLighting}
              loadSavedLighting={loadSavedLighting}
            />

            {/* --- Панель полезных советов --- */}
            <TipsSidebar />

            {/* --- Связанные инструменты --- */}
            {tool && (
              <ToolSidebar 
                currentToolId={tool.id} 
                currentSubToolId={subToolId} 
                tool={tool} 
              />
            )}
          </aside>
        </div>
      </main>

      <Footer />

      {/* --- Global Styles --- */}
      <style jsx global>{`
        /* Base Input Styles */
        .form-input, .form-select, .form-textarea {
          @apply block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-sm focus:border-amber-500 focus:ring-amber-500 sm:text-sm text-gray-900 dark:text-white transition duration-150 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed dark:placeholder-gray-400;
        }
        .form-checkbox {
          @apply h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-amber-600 focus:ring-amber-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        .form-radio {
          @apply h-4 w-4 border-gray-300 dark:border-gray-600 text-amber-600 focus:ring-amber-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        /* Error State Styling */
        .input-error {
          @apply border-red-500 dark:border-red-500 text-red-900 dark:text-red-300 placeholder-red-400 dark:placeholder-red-600 focus:border-red-500 dark:focus:border-red-500 focus:ring-red-500 dark:focus:ring-red-500;
        }
      `}</style>
    </div>
  );
};

export default LightingCalculatorPage;